import {useState} from 'react'
import {formatDisplayDateTime} from '@/dayjs'
import {TKey, useTranslation} from '@/translations/i18n'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Badge} from '@/components/ui/badge/Badge'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {AlertCircleIcon, ChevronDownIcon, MessageChatSquareIcon, RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {useGetBugReportIssues} from '../../hooks/useGetBugReportIssues'
import {BugReportIssue, BugReportIssueStateType} from '../../types'
import {BugReportCommentsDialog} from '../BugReportCommentsDialog/BugReportCommentsDialog'
import {Markdown} from './Markdown'
import {
    StyledChevron,
    StyledCommentsTrigger,
    StyledCompletedAt,
    StyledEmptyBody,
    StyledHistoryError,
    StyledHistoryList,
    StyledHistoryRoot,
    StyledHistoryState,
    StyledIssueBody,
    StyledIssueCard,
    StyledIssueHeader,
    StyledIssueHeaderMain,
    StyledIssueHeaderRight,
    StyledIssueMeta,
    StyledIssueTitle,
    StyledStatus,
    StyledStatusDot
} from './style'

const ASSIGNEE_VISIBLE_STATE_TYPES: BugReportIssueStateType[] = ['started', 'completed']

const STATE_TYPE_TRANSLATION_KEY: Record<BugReportIssueStateType, TKey> = {
    triage: 'bug_reporter:status_triage',
    backlog: 'bug_reporter:status_backlog',
    unstarted: 'bug_reporter:status_unstarted',
    started: 'bug_reporter:status_started',
    completed: 'bug_reporter:status_completed',
    canceled: 'bug_reporter:status_canceled'
}

const IssueCard = ({
    issue,
    onOpenComments
}: {
    issue: BugReportIssue
    onOpenComments: (issue: BugReportIssue) => void
}) => {
    const {t} = useTranslation()
    const [open, setOpen] = useState(false)
    const description = issue.description?.trim()
    const showAssignee = issue.assignee && issue.state && ASSIGNEE_VISIBLE_STATE_TYPES.includes(issue.state.type)

    const toggleOpen = () => setOpen(prev => !prev)

    return (
        <StyledIssueCard>
            <StyledIssueHeader
                role="button"
                tabIndex={0}
                onClick={toggleOpen}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        toggleOpen()
                    }
                }}
                aria-expanded={open}
            >
                <StyledIssueHeaderMain>
                    <StyledIssueTitle>{issue.title}</StyledIssueTitle>
                    <StyledIssueMeta>
                        <span>{issue.identifier}</span>
                        <span>·</span>
                        <span>{formatDisplayDateTime(issue.createdAt)}</span>
                    </StyledIssueMeta>
                </StyledIssueHeaderMain>
                <StyledIssueHeaderRight>
                    {showAssignee && issue.assignee && (
                        <Tooltip
                            content={issue.assignee.name}
                            variant="light"
                            triggerProps={{asChild: true, onClick: event => event.stopPropagation()}}
                        >
                            <Avatar
                                name={issue.assignee.name}
                                imageUrl={issue.assignee.avatarUrl ?? undefined}
                                size="xs"
                            />
                        </Tooltip>
                    )}
                    {issue.state && (
                        <StyledStatus>
                            <StyledStatusDot $color={issue.state.color} />
                            {t(STATE_TYPE_TRANSLATION_KEY[issue.state.type])}
                            {issue.state.type === 'completed' && issue.completedAt && (
                                <StyledCompletedAt>· {formatDisplayDateTime(issue.completedAt)}</StyledCompletedAt>
                            )}
                        </StyledStatus>
                    )}
                    {issue.commentCount > 0 && (
                        <StyledCommentsTrigger>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                shape="square"
                                aria-label={t('bug_reporter:comments_count', {count: issue.commentCount})}
                                onClick={event => {
                                    event.stopPropagation()
                                    onOpenComments(issue)
                                }}
                            >
                                <MessageChatSquareIcon size={16} />
                            </Button>
                            <Badge variant="neutral" size="sm">
                                {issue.commentCount}
                            </Badge>
                        </StyledCommentsTrigger>
                    )}
                    <StyledChevron $open={open}>
                        <ChevronDownIcon size={16} />
                    </StyledChevron>
                </StyledIssueHeaderRight>
            </StyledIssueHeader>
            {open && (
                <StyledIssueBody>
                    {description ? (
                        <Markdown content={description} />
                    ) : (
                        <StyledEmptyBody>Nessuna descrizione</StyledEmptyBody>
                    )}
                </StyledIssueBody>
            )}
        </StyledIssueCard>
    )
}

export const BugReportHistory = ({enabled}: {enabled: boolean}) => {
    const {data: issues, isLoading, isError, error, refetch, isFetching} = useGetBugReportIssues({enabled})
    const [commentsIssue, setCommentsIssue] = useState<BugReportIssue | null>(null)

    if (isLoading) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryState>
                    <Spinner size={24} />
                    Caricamento segnalazioni…
                </StyledHistoryState>
            </StyledHistoryRoot>
        )
    }

    if (isError) {
        const message = error instanceof Error ? error.message : 'Errore sconosciuto'
        return (
            <StyledHistoryRoot>
                <StyledHistoryError role="alert">Impossibile caricare lo storico: {message}</StyledHistoryError>
                <Button type="button" variant="secondary" size="sm" onClick={() => refetch()}>
                    <RefreshCw01Icon size={16} />
                    Riprova
                </Button>
            </StyledHistoryRoot>
        )
    }

    if (!issues?.length) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryState>
                    <AlertCircleIcon size={24} />
                    Nessuna segnalazione inviata al team di sviluppo.
                </StyledHistoryState>
            </StyledHistoryRoot>
        )
    }

    return (
        <StyledHistoryRoot>
            <StyledHistoryList>
                {issues.map(issue => (
                    <IssueCard key={issue.id} issue={issue} onOpenComments={setCommentsIssue} />
                ))}
            </StyledHistoryList>
            <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? <Spinner size={16} /> : <RefreshCw01Icon size={16} />}
                Aggiorna
            </Button>
            <BugReportCommentsDialog
                issue={commentsIssue}
                isOpen={!!commentsIssue}
                onClose={() => setCommentsIssue(null)}
            />
        </StyledHistoryRoot>
    )
}
